import { GetFolderResponse, ObjectId, TopicSearchResult } from '../types';
import { LitElement, css, html } from 'lit';
import { customElement, property, query, state } from 'lit/decorators';
import { debounce, isDefined } from '../helpers/utils';

import { Dialog } from '@material/mwc-dialog';
import { StateController } from '../helpers/state/state-management/state-controller';
import { TextField } from '@material/mwc-textfield';
import accountInfo from '../helpers/state/account-info';
import featuresInfo from '../helpers/state/features-info';
import topicData from '../helpers/state/topic-data';

@customElement('backpack-cards')
export class BackpackCards extends LitElement {
    private readonly amplitude_callsite: string = 'Folder Selector';



    @query('#searchText')
    private readonly searchTextElement?: TextField;

    @query('#searchTextMobile')
    private readonly searchTextElementMobile?: TextField;

    @query('#deleteFolderModalComponent')
    deleteFolderModalComponent!: Dialog;

    @property({ type: Array })
    topics: TopicSearchResult[] = [];

    @property({ type: Boolean})
    onboarding: boolean = false;

    @property({ type: Array })
    folders: GetFolderResponse[] = [];

    @property({ type: Boolean })
    hideActions: boolean = false;

    @property({ type: Number })
    widthPercentage?: number;

    @property({ type: Boolean })
    isBackpackPage: boolean = true;

    @state()
    selectedMyMaps: boolean = false;

    @state()
    selectedBookmarks: boolean = false;

    @state()
    filteredTopics: TopicSearchResult[] = [];

    @state()
    bookmarkedTopics: TopicSearchResult[] = [];

    @state()
    topicsToRender: TopicSearchResult[] = [];

    @state()
    viewType?: string;

    @state()
    page: number = 0;

    @state()
    showPaginationBtn: boolean = false;

    @state()
    sortParameters: any = {
        title: 0,
        pointCount: 0,
        topicViews: 0,
        challengeViews: 0,
        lastEdited: 1,
    };

    @state()
    simpleView: boolean = false;

    readonly pageLength: number = 20;

    private readonly bindAccountState = new StateController(this, accountInfo);

    async updated(changedProperties: Map<string | number | symbol, unknown>) {
        if (changedProperties.has('topics')) {
            this.refreshFilteredTopics();
        }
    }

    private fetchMoreTopics() {
        this.page++;
        this.refreshFilteredTopics();
    }

    private changeSortDirection(field: string) {
        Object.entries(this.sortParameters).forEach(([key, value]) => {
            if (key === field) {
                this.sortParameters[field] = (Number(this.sortParameters[field]) + 1) % 3;
            } else {
                this.sortParameters[key] = 0;
            }
        });
        this.refreshFilteredTopics(field);
    }

    private onLockClick() {
        this.dispatchEvent(new CustomEvent('on-lock-click'));
    }

    private refreshFilteredTopics(sortByField?: string) {
        const getFiledNameBySortKey = (sortKey: string, topic: TopicSearchResult) => {
            switch (sortKey) {
                case 'title': return topic?.name;
                case 'pointCount': return topic?.statistics?.approximate_point_count;
                case 'topicViews': return topic?.statistics?.approximate_topic_views;
                case 'challengeViews': return topic?.statistics?.approximate_challenge_views ?? 0;
                default: return topic?.updated_at;
            }
        };
        if (isDefined(sortByField)) {
            this.topics.sortBy((topic) => getFiledNameBySortKey(sortByField, topic), this.sortParameters[sortByField] === 1 ? 1 : -1);

        } else {
            this.topics.sortBy((topic) => topic.updated_at, 1);
        }

        this.filteredTopics = this.topics;
        this.topicsToRender = this.filteredTopics.slice(0, (this.page + 1) * this.pageLength);
        this.showPaginationBtn = this.filteredTopics.length > ((this.page + 1) * this.pageLength);
    }

    private readonly onSearchText = debounce(this.searchTextInternal, 200);

    private searchTextInternal() {
        let searchText: string = '';

        // we need different elements for desktop and mobile based on where they are
        if (isDefined(this.searchTextElement)) {
            searchText = this.searchTextElement.value?.trim().toLocaleLowerCase() ?? '';
        }

        if (searchText === '' && isDefined(this.searchTextElementMobile)) {
            searchText = this.searchTextElementMobile.value?.trim().toLocaleLowerCase() ?? '';
        }

        if (searchText !== '') {
            this.topicsToRender = this.filteredTopics.filter(topic => {
                return topic.name.toLocaleLowerCase().includes(searchText);
            });
        }
        else {
            this.topicsToRender = this.filteredTopics.slice(0, (this.page + 1) * this.pageLength);
        }
    }

    private onDeleteTopic(e: CustomEvent) {
        const topics = this.topics.filter((topic) => topic._id.$oid !== e.detail.deletedTopicId);

        this.dispatchEvent(new CustomEvent('on-update-topics', {
            detail: {
                topics
            }
        }));
    }

    private onMoveTopic(e: CustomEvent) {
        const topics = this.topics.filter((topic) => topic._id.$oid !== e.detail.movedTopicId);

        this.dispatchEvent(new CustomEvent('on-update-topics', {
            detail: {
                topics
            }
        }));
    }

    private renderSortIcon(field: string) {
        if (this.sortParameters[field] === 0) return html`<mwc-icon></mwc-icon>`;
        if (this.sortParameters[field] === 1) return html`<mwc-icon>arrow_downward</mwc-icon>`;
        return html`<mwc-icon>arrow_upward</mwc-icon>`;
    }

    private openExampleMap(link: string) {
        window.open(link, '_blank');
    }

    private copyCategories(topic: TopicSearchResult) {
        this.dispatchEvent(new CustomEvent('on-copy-categories', {
            detail: {
                categories: topic.categories
            }
        }));
    }

    isExternalMap(organization_id: ObjectId | undefined): boolean {
        return isDefined(organization_id) && organization_id.$oid !== topicData.topic?.organization_id?.$oid;
        // this organization id is different from the one in the topic
        // this is often from bookmarked or purchased maps
    }

    render() {
        const internalContent = html`
            ${this.topicsToRender.map((topic) => {
                return html`
                    <div class="column-item" style="${isDefined(this.widthPercentage) ? `width:${this.widthPercentage}%` : ''}">
                        <backpack-card
                            ?isOnly=${this.topicsToRender.length === 1}
                            @on-delete-topic=${this.onDeleteTopic}
                            @on-move-topic=${this.onMoveTopic}
                            .viewType=${this.viewType}
                            .folders=${this.folders}
                            .hideMetadata=${this.simpleView}
                            .hideViewerType=${this.simpleView}
                            ?hideActions=${this.hideActions}
                            ?onboarding=${this.onboarding}
                            .topic=${topic}>
                        </backpack-card>
                        ${this.onboarding ? html`
                            <div class="overlay">
                                <form-button size="xs" class="overlay-button" @click=${() => this.openExampleMap(topic.discover_details_link ?? '')}>Open Map</form-button>
                                <form-button size="xs" class="overlay-button" @click=${() => this.copyCategories(topic)}>Copy Categories</form-button>
                            </div>
                        ` : ''}
                        ${!this.isExternalMap(topic.organization_id) && this.isBackpackPage && !featuresInfo.backpackEnabled && topic.statistics?.approximate_point_count > 25 ? html`
                            <div class="overlay" @click=${() => this.onLockClick()} style="cursor: pointer;">
                                <form-button size="xs" class="overlay-button" @click=${this.onLockClick}>
                                    <mwc-icon>lock</mwc-icon>
                                </form-button>
                            </div>
                        ` : ''}
                    </div>`;
            })}`;
        const paginator = html`
            ${this.showPaginationBtn ?
                html`<mwc-button raised @click="${this.fetchMoreTopics}" class="moreTopics">
                        View More Maps
                    </mwc-button>`
                : ''}`;

        return html`
            ${this.viewType !== 'cards-part' && !this.simpleView ? html`
                <div class="form-input-button-wrapper">
                    <form-input-button
                        id="searchText"
                        readonly="false"
                        placeholder="What are you looking for?"
                        @submit="${this.onSearchText}"
                    >
                        Search
                    </form-input-button>
                </div>
            ` : ''}

            ${this.viewType !== 'list' ? html`
                <div class="backpackWrapper ${this.onboarding ? 'inspiration-list' : ''}">
                    ${internalContent}
                    ${paginator}
                </div>
            ` : ''}

            ${this.viewType === 'list' ? html`
                <div class="backpackTable">
                    <div class="tr thead">
                        <div class="th" @click=${() => this.changeSortDirection('title')}>
                            Map Title ${this.renderSortIcon('title')}
                        </div>
                        <div class="th desktopOnly" @click=${() => this.changeSortDirection('pointCount')}>
                            ${topicData.topic?.point_name ?? 'Point'}s ${this.renderSortIcon('pointCount')}
                        </div>
                        <div class="th desktopOnly" @click=${() => this.changeSortDirection('topicViews')}>
                            Views ${this.renderSortIcon('topicViews')}
                        </div>
                        <div class="th desktopOnly" @click=${() => this.changeSortDirection('challengeViews')}>
                            Challenges ${this.renderSortIcon('challengeViews')}
                        </div>
                        <div class="th desktopOnly" @click=${() => this.changeSortDirection('lastEdited')}>
                            Last Edited ${this.renderSortIcon('lastEdited')}
                        </div>
                        <div class="th"></div>
                    </div>
                    ${internalContent}
                </div>
                ${paginator}
            ` : ''}
        `;
    }

    static get styles() {
        return css`
            .backpackWrapper {
                width: 100%;
                box-sizing: border-box;
                margin: 0px -10px;
                display: flex;
                flex-wrap: wrap;
                overflow-y: auto; // DO NOT REMOVE - this allows scrolling on mobile
            }

            .form-input-button-wrapper {
                max-width: 500px;
                margin-bottom: 1rem;
            }

            .column-item {
                width: 25%;
                min-width: 100px;
                padding: 10px;
                box-sizing: border-box;
                position: relative;
            }

            .overlay {
                position: absolute;
                top: 10px;
                width: calc(100%);
                display: flex;
                justify-content: center;
                align-items: center;
                opacity: 0;
                transition: opacity 0.3s ease 0s;
                flex-direction: column;
                border-radius: 8px;
                gap: 5px;
            }

            .column-item:hover .overlay {
                opacity: 1;
                background-color: rgba(0, 0, 0, 0.5);
            }

            .overlay-button {
                margin: 0 10px;
            }

            .filtersWrapper {
                padding-bottom: 10px;
                margin: 0px auto;
                display: flex;
                --mdc-theme-secondary: var(--main-primary-color);
                justify-content: space-between;
            }

            .filtersWrapper .filterItem {
                cursor: pointer;
                padding: 16px;
                margin-right: 0.75rem;
                font-size: 1rem;
                border-radius: 5px;
                display: flex;
                align-items: center;
                background: var(--theme-lightest-color);
                transition: 0.3s all ease;
                height: fit-content;
            }

            .filtersWrapper .filterItem:hover {
                background: var(--theme-input-border-color);
            }

            .filtersWrapper .filterItem mwc-icon {
                --mdc-icon-size: 1.25rem;
                margin-right: 0.25rem;
            }

            .createNewMap {
                box-shadow: rgb(31 27 45 / 8%) 0px 2px 2px -2px, rgb(31 27 45 / 8%) 0px 4px 12px;
                border-radius: 6px;
                padding: 1.2rem;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                height: 100%;
                box-sizing: border-box;
            }

            .createTitle {
                color: var(--theme-input-color);
                font-weight: 700;
                cursor: pointer;
            }

            .createTemplate {
                font-size: 1rem;
                font-weight: 700;
                color: var(--theme-input-color);
            }

            .addIcon {
                color: var(--main-primary-color);
                cursor: pointer;
            }

            .backpackTable {
                border-collapse: collapse;
                margin: 1rem 0;
                padding: 0;
                width: 100%;
                box-sizing: border-box;
                overflow: auto;
            }

            .backpackTable .column-item {
                width: 100%;
                min-width: auto;
                padding: 0;
                box-sizing: border-box;
            }

            .backpackTable .thead {
                border-top-left-radius: 1rem;
                border-top-right-radius: 1rem;
                border: 1px solid var(--theme-border-color);
                display: flex;
                padding: 1rem 1.5rem;
            }

            .backpackTable .th {
                flex: 1;
                cursor: pointer;
                display: flex;
                align-items: center;
            }

            .mapSearch {
                margin-bottom: 1rem;
            }

            .searchwrapper {
                display: flex;
                flex-direction: row;
                align-items: center;
            }

            .inspiration-list {
                margin: 0;
            }

            @media (min-width: 551px) {
                .desktopOnly {
                    display: block;
                }

                .addIcon {
                    --mdc-icon-size: 110px;
                }

                .mobileOnly {
                    display: none;
                }

                .createTitle {
                    font-size: 30px;
                    margin-top: 1rem;
                    margin-bottom: 2rem;
                }
            }

            @media (max-width: 550px) {
                .desktopOnly {
                    display: none;
                }

                .backpackTable .thead {
                    display: none;
                }

                .mobileOnly {
                    display: block;
                }

                .createNewMap {
                    display: none;
                }

                .mobileSearch {
                    width: 100%;
                }

                .addIcon {
                    --mdc-icon-size: 40px;
                }

                .column-item {
                    width: 50%;
                    min-width: 150px;
                }
            }
        `;
    }
}
